
import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 py-4">
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-medium text-gray-900">{question}</h3>
        <span className="ml-6 flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-indigo-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </span>
      </button>
      {isOpen && (
        <div className="mt-3 pr-12">
          <p className="text-base text-gray-600">{answer}</p>
        </div>
      )}
    </div>
  );
}

export function FAQSection() {
  const { translate } = useLanguage();
  
  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about SleepNest and how it can transform your sleep coaching practice
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <FAQItem 
              question="How does SleepNest help sleep coaches work with clients?"
              answer="SleepNest provides a seamless platform for sleep coaches to monitor and analyze client sleep data in real-time. Parents track sleep patterns through a simple app, and coaches instantly receive organized data with AI-powered insights, eliminating hours of spreadsheet work."
            />
            <FAQItem 
              question="What is a CRM and why do sleep coaches need one?"
              answer="A CRM (Customer Relationship Management) system helps sleep coaches organize client information, automate communication, track client progress, and manage their business efficiently. It centralizes all client interactions, making it easier to provide personalized care and grow your coaching practice without administrative overload."
            />
            <FAQItem 
              question="How does SleepNest integrate with GoHighLevel for sleep specialists?"
              answer="SleepNest provides sleep specialists with access to the GoHighLevel platform, a powerful all-in-one CRM system customized specifically for sleep coaching businesses. This integration allows you to automate client onboarding, schedule appointments, send automated follow-ups, create marketing campaigns, and manage your entire client journey from a single dashboard."
            />
            <FAQItem 
              question="What business tools are included with the GoHighLevel integration?"
              answer="The GoHighLevel integration includes tools for appointment scheduling, email and SMS marketing, pipeline management, automated workflows, online payment processing, custom forms and questionnaires, website builders, and comprehensive analytics. These tools are pre-configured for sleep coaching businesses, so you can start using them immediately without extensive setup."
            />
            <FAQItem 
              question="Do parents need to install a separate app?"
              answer="No, SleepNest is accessible through any web browser on mobile or desktop devices, with no app installation required. Parents simply login to their account to log sleep data or view coach recommendations."
            />
            <FAQItem 
              question="Is there a limit to how many clients I can work with?"
              answer="No, SleepNest supports unlimited clients on all professional plans. We scale with your practice so you can help as many families as you want without increasing your costs."
            />
            <FAQItem 
              question="How secure is the data on SleepNest?"
              answer="Security is our top priority. All data is encrypted both in transit and at rest, and we comply with healthcare privacy standards. You and your clients maintain complete ownership of all data."
            />
            <FAQItem 
              question="Can I customize the platform for my coaching business?"
              answer="Yes, SleepNest offers white-label options for professional accounts, allowing you to add your logo, brand colors, and customize client communications to match your business identity."
            />
            <FAQItem 
              question="What if I need help using the platform?"
              answer="Our dedicated support team is available via live chat and email. Professional accounts include priority support with guaranteed response times under 2 hours during business hours."
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
